import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Modal from '../../../components/modal/modal.component';
import {StyledFooter} from '../../../components/pane/pane.style';
import {IonItem, IonToast} from '@ionic/react';
import {StyledIonCheckbox} from '../../../components/form/input/checkbox.style';
import {StyledIonLabel} from '../../../components/form/input/label.style';
import {StyledModalContent} from '../../../components/modal/modal.style';
import {Contract} from '../../../models/contract';
import {StyledButton, StyledRedButton} from '../../../components/button/button.style';
import {Preferences} from '@capacitor/preferences';
import {disableSwipeToClose} from '../../../utils/tools/modals';
import {saveAutoContractDocumentsStatement} from '../../../services/contract.service';

type AutoContractSignModalProps = {
    isOpen: boolean;
    contract: Contract | undefined;
    onReject: () => void;
    onConfirm: () => void;
    onClose: () => void;
};

type Step = 'personal_data' | 'declarations' | 'documents_valid';

const AutoContractSignModal: React.FC<AutoContractSignModalProps> = (props) => {
    const {t} = useTranslation();
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [step, setStep] = useState<Step>('personal_data');
    const [workerName, setWorkerName] = useState<string | null>('');
    const [confirmed, setConfirmed] = useState<boolean>(false);
    const [showToast, setShowToast] = useState<boolean>(false);

    useEffect(() => {
        setIsOpen(props.isOpen);
        if (props.isOpen) {
            setStep('personal_data');
            setConfirmed(false);
            Preferences.get({key: 'full_name'}).then(r => setWorkerName(r.value));
        }
    }, [props.isOpen]);

    const handleNext = async () => {
        setConfirmed(false);
        if (step === 'personal_data') {
            setStep('declarations');
        } else if (step === 'declarations') {
            setStep('documents_valid');
        } else {
            try {
                if (props.contract?.id) {
                    await saveAutoContractDocumentsStatement(props.contract.id);
                }
                props.onConfirm();
            } catch (error) {
                setShowToast(true);
            }
        }
    };

    const documentsValidText = props.contract?.auto_contract_statement_content || '';

    return (
        <Modal
            isOpen={isOpen}
            canDismiss={disableSwipeToClose}
            onDidDismiss={() => props.onClose()}>
            <StyledModalContent>

                {step === 'personal_data' && <>
                    <h1 className="title">{t('contractPage.autoContract.sign.personalDataTitle')}</h1>
                    <div className="content">
                        <ul>
                            {props.contract?.params && Object.entries(props.contract.params).map(([key, val], i) => (
                                <li key={i}>{t('contractPage.contractDataApproval.' + key)}: <strong>{val as string}</strong></li>
                            ))}
                        </ul>
                        <IonItem lines="none">
                            <StyledIonLabel className="ion-text-wrap">
                                {t('contractPage.autoContract.sign.personalDataConfirm', {fullName: workerName})}
                            </StyledIonLabel>
                            <StyledIonCheckbox slot="start" onIonChange={(e) => setConfirmed(e.detail.checked)}/>
                        </IonItem>
                    </div>
                </>}

                {step === 'declarations' && <>
                    <h1 className="title">{t('contractPage.autoContract.sign.declarationsTitle')}</h1>
                    <div className="content">
                        <div dangerouslySetInnerHTML={{__html: props.contract?.declaration_summary_content || ''}}/>
                        <IonItem lines="none">
                            <StyledIonLabel className="ion-text-wrap">
                                {t('contractPage.autoContract.sign.declarationsConfirm')}
                            </StyledIonLabel>
                            <StyledIonCheckbox slot="start" onIonChange={(e) => setConfirmed(e.detail.checked)}/>
                        </IonItem>
                    </div>
                </>}

                {step === 'documents_valid' && <>
                    <h1 className="title">{t('contractPage.autoContract.sign.documentsValidTitle')}</h1>
                    <div className="content">
                        <div dangerouslySetInnerHTML={{__html: documentsValidText}}/>
                        <IonItem lines="none">
                            <StyledIonLabel className="ion-text-wrap">
                                {t('contractPage.autoContract.sign.documentsValidConfirm')}
                            </StyledIonLabel>
                            <StyledIonCheckbox slot="start" onIonChange={(e) => setConfirmed(e.detail.checked)}/>
                        </IonItem>
                    </div>
                </>}

                <StyledFooter className="modal footer no-shadow">
                    <StyledRedButton type="button" onClick={() => props.onReject()}>
                        {t('contractPage.content.reject')}
                    </StyledRedButton>
                    <StyledButton disabled={!confirmed} onClick={handleNext}>
                        {step === 'documents_valid'
                            ? t('contractPage.autoContract.sign.proceed')
                            : t('contractPage.autoContract.sign.next')}
                    </StyledButton>
                </StyledFooter>
            </StyledModalContent>
            <IonToast
                isOpen={showToast}
                onDidDismiss={() => setShowToast(false)}
                message={t('common.error')}
                duration={6000}
                position="top"
                color="danger"
            />
        </Modal>
    );
};

export default AutoContractSignModal;
